import { useMemo } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { DispositiveProvisionsPossibleRecipients_EntityFragment } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/graphql/DispositiveProvisionsFormRecipients.generated';
import {
  createGroupedOptionsForTypeaheadSelect,
  groupEntitiesByCategory,
} from '@/modules/entities/utils/groupEntitiesByCategory';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { EntityStage } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { useLiabilitiesIssuersAndRecipientsQuery } from '../graphql/LiabilityModal.generated';
import { NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL } from '../LiabilityModal.constants';

interface useIssuerAndRecipientOptionsOpts {
  currentEntityId?: string;
}

interface useIssuerAndRecipientOptionsReturn {
  options: TypeaheadSelectInputOption<string>[];
  alternateSourceOptions: TypeaheadSelectInputOption<string>[];
  /**
   * The selectable issuer/recipient entities (the options' underlying data,
   * carrying `stage`/`kind`/`subtype`). Lets a caller look a selected
   * issuer/recipient back up to act on it — e.g. activate a PreCreated entity
   * on save — without depending on the estate-holdings context.
   */
  entities: DispositiveProvisionsPossibleRecipients_EntityFragment[];
}

export function useIssuerAndRecipientOptionsGrouped({
  currentEntityId,
}: useIssuerAndRecipientOptionsOpts): useIssuerAndRecipientOptionsReturn {
  const { householdId } = useHouseholdDetailsContext();

  const { data: clientData } = useLiabilitiesIssuersAndRecipientsQuery({
    variables: {
      where: { id: householdId },
    },
  });

  const household = getNodes(clientData?.households)[0];

  return useMemo(() => {
    if (!household) {
      return {
        options: [],
        alternateSourceOptions: [],
        entities: [],
      };
    }

    const entityRecipients = getNodes(household.entities);

    // Filter entities based on stage (same logic as before)
    const filteredEntities = entityRecipients.filter((e) => {
      // Include Active entities always, and PreCreated entities when balance sheet data organization is enabled
      return (
        e.stage === EntityStage.Active || e.stage === EntityStage.PreCreated
      );
    });

    // Group entities into categories
    const groupedEntities = groupEntitiesByCategory(
      filteredEntities,
      currentEntityId
    );

    // Create options with group names for TypeaheadSelectInput
    const entityRecipientOptions =
      createGroupedOptionsForTypeaheadSelect(groupedEntities);

    const options: TypeaheadSelectInputOption<string>[] = [
      EXTERNAL_OPTION,
    ].concat(entityRecipientOptions);

    // the "alternate payment source options" are the same as the entity recipient options
    // (all household entities except the current entity), but with no external option
    return {
      options,
      alternateSourceOptions: entityRecipientOptions,
      entities: filteredEntities,
    };
  }, [currentEntityId, household]);
}

const EXTERNAL_OPTION: TypeaheadSelectInputOption<string> = {
  display: 'External',
  value: NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL,
  groupName: '',
};
